/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../reducers';
import Question from './Question';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { getUtrFromSurvey } from '../../../selectors/survey';
import { getCurrentUser, isOrgManager } from '../../../selectors/user';
import { Badge, Button } from 'reactstrap';
import { useQuestionForm } from './QuestionReducer';
import { CommentsInput } from '../../utr-confirmation-modal/Comments';
import { EvidenceInput } from '../../utr-confirmation-modal/EvidenceInput';
import { UpdateActions, saveUniversalTrackerValue } from '../../../actions/universalTrackerValue';
import {
  getStatusReadableText,
  getStatusText,
  isAssuredLocked,
  isNaOrNr,
  isNr,
} from '../../../utils/universalTrackerValue';
import { convertDataForSubmissions, prepareSubmitData } from './questionSubmit';
import QuestionSubmitButtons from './QuestionSubmitButtons';
import { checkHasComments, isDataEmpty } from './questionUtil';
import { NotApplicableTypes, QuestionStatuses, UtrvStatus } from '../../../constants/status';
import { getSurveyAssurancePortfolios } from '../../../actions/assurance';
import { loadUtrvHistory } from '../../../actions/universalTracker';
import { handleRouteError, loggerMessage } from '../../../logger';
import { EvidenceFile, QuestionProps } from './questionInterfaces';
import { useHasCommentsEvidenceChanged, useHasUnitNumberScaleChanged, useHasValueChanged } from './useHasChanged';
import LoadingPlaceholder from '../../LoaderContainer/LoadingPlaceholder';
import { generateDisabledUtrs, hasUTRVFilter } from '../utils/getDisableUtrs'
import { BlueprintContributions, ScopeQuestionGroup } from '../../../types/survey';
import { loadGlossary } from '../../../slice/glossarySlice';
import SDGContributionModal, { SDGContribution } from './SDGContributionModal';
import { SDGGoal, sdgMap } from '../../../constants/sdg-data';
import queryString from 'query-string';
import { CollapseButton, CollapseContent, CollapsePanel } from '../../collapse-panel';
import QuestionSubtitle from './toolbar/QuestionSubtitle';
import SimpleTooltip from '../../simple-tooltip';
import { getSubmitErrorMessage } from '../../../actions/error';
import UtrvComments from '../../utrv-comments';
import G17Client from '../../../services/G17Client';
import { SurveyActionData } from '../../../model/surveyData';
import { SessionStorage } from '../../../services/SessionStorage';
import './QuestionContainer.scss';
import { QuestionStatusBadge } from './QuestionStatusBadge';
import { useQuestionIds } from '../../../hooks/useQuestionIds';
import { TOOLTIP } from '../../../constants/labels';
import { useGetCustomTagsQuery } from '../../../api/metric-groups';
import { TagList } from './tags/TagList';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { QuestionRecommendations } from './QuestionRecommendations';
import { useGetInitiativeUniversalTrackersQuery } from '../../../api/initiative-universal-trackers';
import { useBookmarks } from '../../survey-question-list/hooks/useBookmarks';
import { CongratulationsModal } from '../../complete-survey-modal/CongratulationsModal';
import { useToggleCompleteSurveys } from '../../../hooks/useToggleCompleteSurveys';
import { SurveyPermissions } from '../../../services/permissions/SurveyPermissions';
import { useToggleCongratulationsModal } from '../../complete-survey-modal/useToggleCongratulationsModal';
import { RichTextEditorContainer } from '../../rich-text-editor/RichTextEditor';
import { NoteInstructionsViewer } from '../../../features/note-instructions';
import { InitiativeData } from '../../../types/initiative';
import { Document } from 'flexsearch';
import NotFound from '../../not-found';
import { isAggregatedSurvey, isAutoAggregatedSurvey } from '../../../utils/survey';

const { UPDATE } = UpdateActions.STATUS;

interface QuestionContainerConfigInterface {
  enableFurtherExplanation?: boolean;
  enableEvidence?: boolean;
  enableNANR?: boolean;
  enableMakePrivate?: boolean;
  enableQuestionSubtitle?: boolean;
  enableComments?: boolean;
};

interface QuestionContainerProps {
  initiativeId: string;
  surveyId: string;
  questionId: string;
  questionIndex: string;
  surveyGroups: ScopeQuestionGroup[];
  survey: SurveyActionData;
  materiality: InitiativeData['materiality'];
  blueprint: BlueprintContributions;
  searchIndex?: Document<unknown, false>;
  handleReload: () => Promise<any>;
  config?: QuestionContainerConfigInterface;
}

enum ValidationErrors {
  EvidenceRequired = 'You must supply evidence files to support your data',
  NoteRequired = 'You must add something in the Further explanation/Notes section',
}

interface SdgContributionModal {
  isOpen: boolean,
  contributionData?: SDGContribution
}

const getConfig = (config?: QuestionContainerConfigInterface): Required<QuestionContainerConfigInterface> => {
  return {
    enableFurtherExplanation: config?.enableFurtherExplanation ?? true,
    enableEvidence: config?.enableEvidence ?? true,
    enableNANR: config?.enableNANR ?? true,
    enableMakePrivate: config?.enableMakePrivate ?? true,
    enableQuestionSubtitle: config?.enableQuestionSubtitle ?? true,
    enableComments: config?.enableComments ?? true,
  }
}

export function QuestionContainerStateless(props: QuestionContainerProps) {
  const {
    initiativeId,
    surveyId,
    questionId,
    questionIndex,
    surveyGroups,
    survey,
    materiality,
    blueprint,
    searchIndex,
    handleReload,
  } = props;

  const config = getConfig(props.config);

  // @TODO - Remove these hooks
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const ref = useRef<null | HTMLElement>(null);

  const scrollToRef = () => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
      });
    }
  }

  const user = useAppSelector(getCurrentUser);
  const isOrganizationManager = useAppSelector(isOrgManager);

  const isSurveyManager = !!(survey && user && SurveyPermissions.canManage(survey, user));
  const { data: initiativeUniversalTrackers = [] } = useGetInitiativeUniversalTrackersQuery(initiativeId, { skip: !initiativeId });
  const { bookmarks } = useBookmarks(surveyId);
  const { data: tags = [] } = useGetCustomTagsQuery(initiativeId, { skip: !initiativeId });
  const { utr, utrv } = getUtrFromSurvey({ survey, surveyGroups, questionId, initiativeUniversalTrackers });

  const [openComments, setOpenComments] = useState(false);
  const [sdgContribution, setSDGContribution] = useState<SdgContributionModal>({
    isOpen: false,
    contributionData: undefined
  });

  const toggleContributionModal = () => setSDGContribution({
    isOpen: !sdgContribution.isOpen,
    contributionData: undefined
  });

  const [commentId, setCommentId] = useState('');

  const { toggleCompleteSurveys } = useToggleCompleteSurveys({
    handleReload,
    surveys: [survey],
    isCompleting: true,
  });

  const { isCongratulationsModalOpen, openCongratulationsModal, toggleCongratulationsModal } =
    useToggleCongratulationsModal({
      survey,
      currentUser: user,
      toggleCompleteSurveys,
    });

  const disableUtrs = useMemo(() => {
    const query = queryString.parse(location.search, { arrayFormat: 'bracket' });
    if (!hasUTRVFilter(query)) {
      return [];
    }

    return generateDisabledUtrs({
      surveyGroups,
      blueprint,
      materiality,
      searchIndex,
      user,
      ...query,
      filterByBookmarks: query.filterByBookmarks === 'true',
      bookmarks,
    });
  }, [blueprint, bookmarks, location.search, materiality, searchIndex, surveyGroups, user]);

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const id = searchParams.get('commentId');
      if (id) {
        setCommentId(id);
        setOpenComments(!!id);
        searchParams.delete('commentId');
        history.replace({
          pathname: location.pathname,
          search: searchParams.toString(),
        });
      }
    }
  }, [history, location.pathname, location.search]);

  const {
    currentQuestionIndex,
    altCode: alternativeCode,
  } = useQuestionIds(surveyGroups, questionIndex, questionId, disableUtrs)

  useEffect(() => SessionStorage.setQuestionIndex(currentQuestionIndex), [currentQuestionIndex]);

  const {
    state,
    handleComments,
    handleEvidenceLinkAdd,
    handleFilesAdded,
    handleFileRemoved,
    toggleExistingEvidence,
    changeQuestion,
    setDetails,
    update,
    updateTable,
    handleFileDescriptionAdd,
  } = useQuestionForm();

  const {
    comments,
    editorState,
    saving,
    files,
    value,
    details,
    displayCheckbox,
    valueData,
    existingFiles,
    numberScale,
    table,
    unit,
    assurancePortfolioLoaded,
    alternativeCodeOverride,
    canVerify,
    canContribute,
    inputMessage,
  } = state;

  const canViewNoteInstructions = useAppSelector(FeaturePermissions.canSetNoteInstructions);

  React.useEffect(() => {
    if (user && utrv && utr) {
      changeQuestion({ utrv, utr, surveyData: survey, user });
      loadUtrvHistory(utrv._id)
        .then((respData: any) => setDetails({ data: respData, loaded: true }))
        .catch(() => setDetails({ loaded: true }));
    }
  }, [changeQuestion, utrv, utr, survey, setDetails, user]);

  React.useEffect(() => {
    getSurveyAssurancePortfolios(surveyId)
      .then((assurancePortfolio) => {
        update({
          assurancePortfolio,
          hasPortfolio: Boolean(assurancePortfolio?.assurances?.[0]),
          assurancePortfolioLoaded: true
        });
      }).catch((e: Error) => {
        handleRouteError(e);
        update({ assurancePortfolioLoaded: true })
      });
  }, [surveyId, update])

  React.useEffect(() => {
    dispatch(loadGlossary())
  }, [dispatch])

  const { stakeholderHistory, verifierHistory } = React.useMemo(() => {
    const latestHistory = details.data?.latestHistory;
    if (!utrv?.notes) {
      return latestHistory ?? {};
    }

    return {
      stakeholderHistory: utrv.notes.stakeholder ?? latestHistory?.stakeholderHistory,
      verifierHistory: utrv.notes.verifier ?? latestHistory?.verifierHistory,
    };
  }, [details.data?.latestHistory, utrv?.notes]);

  const hasComments = checkHasComments(stakeholderHistory, verifierHistory);

  const isLoaded = user && blueprint && assurancePortfolioLoaded;
  const isUTRLoaded = utr && utrv;

  const hasValueChanged = useHasValueChanged(state, { utrv, utr });
  const hasUnitNumberScaleChanged = useHasUnitNumberScaleChanged(state, { utrv, utr, surveyData: survey });
  const hasCommentsEvidenceChanged = useHasCommentsEvidenceChanged(state);
  const hasAnythingChanged = hasValueChanged || hasCommentsEvidenceChanged;

  if (isLoaded && !isUTRLoaded) {
    // @TODO - Need a way to handle this?
    // No permission or bad UTR

    loggerMessage('No permission or bad UTR', {
      isLoaded,
      utrId: utr?.getId(),
      utrvId: utrv?._id,
    });
    return <NotFound />;
  }

  const isAggregate = isAggregatedSurvey(survey.type);
  const isAutoAggregated = isAutoAggregatedSurvey(survey.type);
  const isCompleted = Boolean(survey.completedDate);
  const enableComments = config.enableComments && !isAggregate;

  const isSpectator = !canContribute && !canVerify;
  const canAutoVerify = canContribute && canVerify;

  const isReadOnly = saving || isSpectator || isAggregate || isCompleted || (utrv && isAssuredLocked(utrv)) || false;
  const isNaNr = utrv ? isNaOrNr(utrv) : false;
  const selectedAltCode = alternativeCodeOverride || alternativeCode || utr?.getType() || '';

  const handleNA = () => handleSubmitQuestion(UPDATE, NotApplicableTypes.na, canAutoVerify);
  const handleNR = () => handleSubmitQuestion(UPDATE, NotApplicableTypes.nr, canAutoVerify);

  const handleAssuranceAction: QuestionProps['handleAssuranceAction'] = (action) => {
    if (saving || !utrv || isCompleted) {
      return false;
    }
    update({ saving: true, saveId: utrv._id, errored: false, message: '' });

    G17Client.utrvAssuranceAction({ utrvId: utrv._id, action })
      .then(() => handleReload())
      .then(() => update({
        saving: false,
        errored: false,
        message: 'Updated assurance status successfully',
      }))
      .catch((e: Error) => {
        handleRouteError(e, { data: action, userId: user?._id });
        update({ saving: false, errored: true, message: e.message });
      });

  };

  const handlePrivacy = () => {
    if (!utrv) {
      return;
    }

    update({ saving: true, saveId: questionId, errored: false, message: '' });

    return G17Client.updateSurveyQuestionFlags({
      surveyId,
      utrvIds: [utrv._id],
      properties: { isPrivate: !utrv.isPrivate },
    })
      .then(() => handleReload())
      .then(() =>
        update({
          saving: false,
          errored: false,
          message: 'Saved Successfully',
          files: [],
          comments: '',
        })
      )
      .catch((e: Error) => {
        handleRouteError(e);
        update({
          saving: false,
          errored: true,
          message: getSubmitErrorMessage(e, []),
        });
      });
  };

  const handleStandardsClick = (code: string) => update({ 'alternativeCodeOverride': code });

  const hasInvalidInput = inputMessage ? Object.keys(inputMessage).some((key) => inputMessage[key]) : false;

  const isFormValid = () => !isDataEmpty({
    utr,
    displayCheckbox,
    valueData,
    value,
    table
  }) && !hasInvalidInput;

  const isNoteRequired = utrv && utrv.noteRequired;

  const handleSubmitQuestion = (action: string, notApplicableType: string = '', autoVerify = false) => {
    if (saving) {
      return false;
    }
    if (!(utrv && utr)) {
      return false;
    }

    const filesToSubmit: EvidenceFile[] = [...files];

    if (UPDATE === action && existingFiles.length > 0) {
      // Can only reuse UPDATED evidence
      filesToSubmit.push(...existingFiles
        .filter((f) => f.status === UtrvStatus.Updated && !f.isDeleted));
    }

    const isEvidenceRequired = (utrv.evidenceRequired && filesToSubmit.length < 1) && !notApplicableType
    if (UPDATE === action && isEvidenceRequired) {
      update({ saving: false, errored: true, message: ValidationErrors.EvidenceRequired });
      return;
    }

    if (UPDATE === action && isNoteRequired && !comments && !notApplicableType) {
      update({ saving: false, errored: true, message: ValidationErrors.NoteRequired });
      return;
    }

    const data = prepareSubmitData({
      utr,
      utrv,
      value,
      unit,
      numberScale,
      files: filesToSubmit,
      comments,
      editorState,
      valueData,
      table,
      displayCheckbox,
      autoVerify,
      notApplicableType,
    })
    const questionId = utr.getId();
    const questionUtrvId = utrv._id;

    update({ saving: true, saveId: questionId, errored: false, message: '' });

    const questionData = convertDataForSubmissions(data)
    const saveUtr = dispatch(saveUniversalTrackerValue(action, questionUtrvId, questionData));

    saveUtr.then((result: { data: { data: SDGContribution[] } }) => {
      const goals: SDGContribution[] = [];
      const targets: SDGContribution[] = [];

      const sortFunction = (a: SDGContribution, b: SDGContribution) => (a.value - (a.oldValue ?? 0)) - (b.value - (b.oldValue ?? 0));
      const goalsAndTargets: SDGGoal[] = sdgMap;
      const results = result.data.data;

      if (Array.isArray(results)) {
        results.forEach((item: SDGContribution) => {
          goalsAndTargets.forEach(code => {
            if (item.universalTracker.code === `sdg/${code.code}`) {
              if (item.value - (item.oldValue ?? 0) >= 1) {
                return goals.push(item)
              }
            }
          })

          goalsAndTargets.forEach(o =>
            o.targets.forEach(code => {
              if (item.universalTracker.code === `sdg/${code.code}`) {
                if (item.value - (item.oldValue ?? 0) >= 1) {
                  return targets.push(item)
                }
              }
            })
          )
        })
        const highestGoal = goals?.sort(sortFunction)[0];
        const highestTarget = targets.sort(sortFunction)[0];

        setSDGContribution({
          isOpen: !!highestGoal || !!highestTarget,
          contributionData: highestGoal ?? highestTarget
        })
      }
    })
      .then(() => handleReload())
      .then(() => update({
        saving: false,
        errored: false,
        message: 'Saved Successfully',
        files: [],
        comments: '',
        editorState: undefined
      }))
      .then(() => {
        openCongratulationsModal({ editingUtrvIds: [questionUtrvId], verified: questionData.autoVerify === 'true' });
      })
      .catch((e: Error) => {
        handleRouteError(e, { data: JSON.stringify(data), userId: user?._id });
        update({
          saving: false,
          errored: true,
          message: getSubmitErrorMessage(e, filesToSubmit)
        });
      })
  }

  const handleSubmitUpdate = (e: React.MouseEvent, verify = false) => {
    e.preventDefault();
    const action = UPDATE;
    update({ action: action })
    return handleSubmitQuestion(action, '', verify)
  };

  const handleToggle = () => {
    setCommentId('');
    setOpenComments(!openComments);
  };

  return (
    <>
      {config.enableComments && utrv && openComments ? <div className='position-relative' style={{ marginTop: '-3px' }}>
        <UtrvComments toggle={handleToggle} utrvId={utrv?._id} commentId={commentId} />
      </div> : null}
      <div className='questionContainer p-3'>
        <div className='d-flex justify-content-between question-header-container'>
          <div>
            {config.enableQuestionSubtitle ?
              <LoadingPlaceholder height={17} isLoading={!utr}>
                <QuestionSubtitle
                  utr={utr}
                  selectedStandard={selectedAltCode}
                  handleStandardsClick={handleStandardsClick}
                  survey={survey}
                />
              </LoadingPlaceholder> : null}
            <span ref={ref} style={{ top: '-6.5rem', position: 'relative' }} />
            {isAggregate ? null : (
              <div className='d-flex align-items-center mb-2'>
                {utrv && (
                  <SimpleTooltip text={getStatusText(utrv)}>
                    <Badge className={`status-info ${utrv.status}`} pill>
                      {getStatusReadableText(utrv.status as UtrvStatus)}
                    </Badge>
                  </SimpleTooltip>
                )}
                <div className='ml-2 d-inline-block'>
                  <Badge
                    color={utrv?.isPrivate ? 'primary' : 'success'}
                    className={`questionContainer__privacy-badge ${utrv?.isPrivate ? QuestionStatuses.Private : ''}`}
                    pill
                  >
                    {utrv?.isPrivate ? QuestionStatuses.Private : QuestionStatuses.Public}
                  </Badge>
                </div>
                {utrv && isNaNr ? (
                  <div className='ml-2 d-inline-block na-nr-info'>
                    <SimpleTooltip text={isNr(utrv) ? 'Enter an answer to start reporting' : 'Enter an answer to make applicable'}>
                      <span className='na-nr-info-top'>
                        {isNr(utrv) ? 'not reportable' : 'not applicable'}
                      </span>
                    </SimpleTooltip>
                  </div>
                ) : null
                }
                <QuestionStatusBadge utrv={utrv} />
              </div>
            )}
          </div>
          {enableComments ?
            <div className='ml-auto text-right'>
              <div className='d-flex justify-content-end align-items-center'>
                <div className='toolbar-button'>
                  <SimpleTooltip text={TOOLTIP.comment}>
                    <Button
                      color='transparent'
                      active={openComments}
                      className='toolbar-status-button comment-btn'
                      disabled={isAggregate}
                      onClick={handleToggle}
                    >
                      <i className='far fa-comment-alt' />
                    </Button>
                  </SimpleTooltip>
                </div>
              </div>
            </div>
            : null}
        </div>

        <Question
          {...state}
          index={0}
          hasChanged={hasAnythingChanged}
          utr={utr}
          utrv={utrv}
          isVerifier={canVerify}
          isContributor={canContribute}
          isOrganizationManager={isOrganizationManager}
          survey={survey}
          update={update}
          updateTable={updateTable}
          alternativeCode={selectedAltCode}
          isAggregate={isAggregate}
          scrollToRef={scrollToRef}
          handleNA={handleNA}
          handleNR={handleNR}
          handleAssuranceAction={handleAssuranceAction}
          handleReject={() => handleSubmitQuestion(UpdateActions.STATUS.REJECT)}
          handleComments={handleComments}
        />

        <QuestionRecommendations utr={utr?.getRaw()} />
        {isAutoAggregated ? (
          <div className='mt-4 text-center text-ThemeTextMedium'>
            This question cannot be modified as it is automatically aggregating its results using subsidiary data
          </div>
        ) : null}

        {isAggregate ? null : (
          <>
            {config.enableFurtherExplanation ?
              <CollapsePanel className='comments mt-3 py-3 border-top' collapsed={!isNoteRequired && !hasComments}>
                <CollapseButton>
                  <h5 className='question-title-container text-ThemeAccentExtradark'>
                    Further explanation / notes {isNoteRequired ? '*' : ''}
                  </h5>
                </CollapseButton>
                <CollapseContent>
                  <div>
                    {canViewNoteInstructions ? (
                      <NoteInstructionsViewer
                        noteInstructionsEditorState={survey.noteInstructionsEditorState}
                        noteInstructions={survey.noteInstructions}
                      />
                    ) : null}
                    <div className='mt-3'>
                      <LoadingPlaceholder height={85} isLoading={!utr}>
                        {utr && utrv ? (
                          <RichTextEditorContainer>
                            <CommentsInput
                              key={utrv._id}
                              state={{
                                value: state.value,
                                valueData: state.valueData,
                                table: state.table,
                                numberScale: state.numberScale,
                                unit: state.unit,
                              }}
                              disabled={isReadOnly}
                              hideLabel
                              comments={comments}
                              isVerifier={canVerify}
                              isContributor={canContribute}
                              utrv={utrv}
                              stakeholderHistory={stakeholderHistory}
                              verifierHistory={verifierHistory}
                              handleComments={handleComments}
                            />
                          </RichTextEditorContainer>
                        ) : (
                          <></>
                        )}
                      </LoadingPlaceholder>
                    </div>
                  </div>
                </CollapseContent>
              </CollapsePanel>
              : null}

            {config.enableEvidence && utr && utrv ? <LoadingPlaceholder height={172} isLoading={!utr}>
              <EvidenceInput
                disabled={isReadOnly}
                handleEvidenceLinkAdd={handleEvidenceLinkAdd}
                handleFilesAdded={handleFilesAdded}
                handleFileRemoved={handleFileRemoved}
                files={files}
                details={details}
                isVerifier={canVerify}
                isContributor={canContribute}
                existingFiles={existingFiles}
                evidenceInstructions={utr.getEvidenceInstructions(selectedAltCode)}
                handleExistingFileRemoved={toggleExistingEvidence}
                isEvidenceRequired={utrv.evidenceRequired}
                handleFileDescriptionAdd={handleFileDescriptionAdd}
              />
            </LoadingPlaceholder>
              : null}

            <div className='button-container mt-5' id='scrollConfirmVerify'>
              <LoadingPlaceholder height={29} isLoading={!isLoaded} className='mb-0'>
                {isCompleted
                  ?
                  <div className='mt-4 text-center'>This survey is marked as <span className='btn btn-success btn-sm'>
                    <i className='fa fa-lock mr-2' />Completed</span> and can no longer be modified.
                  </div>
                  :
                  isLoaded && utr && utrv ? <QuestionSubmitButtons
                    utrv={utrv}
                    utr={utr}
                    isVerifier={canVerify}
                    isContributor={canContribute}
                    isSurveyManager={isSurveyManager}
                    valueHasChanged={hasAnythingChanged || hasUnitNumberScaleChanged}
                    commentsEvidenceHasChanged={hasCommentsEvidenceChanged}
                    formValid={isFormValid()}
                    disabled={isReadOnly}
                    handleSubmitUpdate={handleSubmitUpdate}
                    handleSubmitQuestion={handleSubmitQuestion}
                    handleNA={config.enableNANR ? handleNA : null}
                    handleNR={config.enableNANR ? handleNR : null}
                    handlePrivacy={config.enableMakePrivate ? handlePrivacy : null}
                  />
                    :
                    null
                }
              </LoadingPlaceholder>
            </div>
          </>
        )}
        <TagList tags={tags} utrId={utr?.getId() ?? ''} />
      </div>

      <CongratulationsModal isOpen={isCongratulationsModalOpen} toggle={toggleCongratulationsModal} />

      <div>
        {sdgContribution.contributionData ? <SDGContributionModal
          isOpen={sdgContribution.isOpen}
          toggle={toggleContributionModal}
          sdgContribution={sdgContribution.contributionData}
        /> : null}
      </div>

      <Prompt
        when={hasAnythingChanged}
        message={() => 'Are you sure you want to leave this page? Your unconfirmed data will be lost.'} />
    </>
  );
}


